import { useEffect, useState, useCallback } from "react";
import { useSession, LogoutReason } from "../Auth/hooks/useSession";
import AuthLogoutMessage from "../Auth/components/AuthLogoutMessage";
import { getFetchOptions } from "../../utils/fetch";
import useFetch from "../../hooks/useFetch";
import useFetchAuto from "../../hooks/useFetchAuto";
import LoadingSpinner from "../../components/loadingSpinner/LoadingSpinner";
import Modal from "../../components/modals/Modal";
import MessageModal, { MessageModalType } from "../../components/modals/MessageModal";
import Button, { ButtonType } from "../../components/Button";
import { faEnvelope, faCircleCheck } from "@fortawesome/free-solid-svg-icons";
import SelectPackages from "../../components/packages/SelectPackages";
import EditCustomerUser from "./EditCustomerUser";
import CustomerUsersPanel from "./CustomerUsersPanel";
import { CompanyUserRole, AssignableRoleOptionsBase, roleLabel } from "../../utils/companyUserRole";
import useSignalR from "../../hooks/useSignalR";
import useSignalRGroupHandler from "../../hooks/useSignalRGroupHandler";
import { getUserEmail } from "../../utils/getUserInformation";
import {
    normaliseUsername,
    mapLoadedUserToRow,
    mapPendingUserToRow,
    mapCompletedPendingUserRow,
    getUserFullName,
    mapAddUserRequestBody,
    mapUpdateUserRequestBody,
    mapDeleteUserRequestBody,
    mapUserToCleanUser
} from "./ViewCustomer.helpers";
import "./ViewCustomer.scss";

const MODAL = {
    NONE: null,
    CONFIRM_DELETE_CUSTOMER: "CONFIRM_DELETE_CUSTOMER",
    CONFIRM_RESEND: "CONFIRM_RESEND",
    ERROR: "ERROR",
    FATAL_ERROR: "FATAL_ERROR",
    CONFIRM_DELETE_USER: "CONFIRM_DELETE_USER",
    CONFIRM_SAVE: "CONFIRM_SAVE",
    EDIT_USER: "EDIT_USER",
    ADD_USER: "ADD_USER"
};

const INITIAL_DATA = {
    businessName: { value: "", validation: 0 },
    businessAddress: { value: "", validation: 0 },
    userFirstName: { value: "", validation: 0 },
    userLastName: { value: "", validation: 0 },
    userEmail: { value: "", validation: 0 },
    currentCustomerPackages: []
};

const ViewCustomer = (props) => {
    // State and hooks
    const [packageData, setPackageData] = useState(INITIAL_DATA);
    const { reason } = useSession();
    const [resending, setResending] = useState(false);
    const [users, setUsers] = useState([]);
    const { loading: loadingCustomer, data } = useFetchAuto("/api/CustomerById/" + props.item.Id);
    const { loading: loadingPackages, data: availablePackages } = useFetchAuto("/api/ListAssignablePackages/");
    const { ready, execute, executingFetch } = useFetch();
    const [customerAdminHasLoggedIn, setCustomerAdminHasLoggedIn] = useState(false);
    const { disableUserModification } = props;
    const [errorMessage, setErrorMessage] = useState("");
    const [errorTitle, setErrorTitle] = useState("Error");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [canSubmit, setCanSubmit] = useState(false);

    const [selectedUser, setSelectedUser] = useState(null);
    const [userPendingDelete, setUserPendingDelete] = useState(null);
    const [pendingSave, setPendingSave] = useState(null);
    const [activeModal, setActiveModal] = useState(MODAL.NONE);
    const [packageVersion, setPackageVersion] = useState(0);

    const { connection, registerHandler } = useSignalR();
    const [username] = useState(() => getUserEmail());

    // Derived values
    const signalRGroupName = username ? `CustomerUsers-${username}` : null;

    // Shared helpers
    const showError = useCallback((title, message) => {
        setErrorTitle(title ?? "Error");
        setErrorMessage(message ?? "An unexpected error occurred.");
        setActiveModal(MODAL.ERROR);
    }, []);

    const closeActiveModal = useCallback(() => {
        setActiveModal(MODAL.NONE);
    }, []);

    // SignalR handlers
    const handleUserAddedResultMessage = useCallback((message) => {
        const payload = message?.payload;
        const username = normaliseUsername(payload?.username);

        if (!message.success) {
            setUsers((prev) => {
                return prev.filter((user) => {
                    const isMatch = normaliseUsername(user?.Username) === username;
                    return !(isMatch && user?.IsPendingCreate === true);
                });
            });

            showError("User creation failed", "There has been an error and iTEL has been notified for investigation.");
            return;
        }

        if (!username) {
            return;
        }

        const newId = payload?.id;

        if (newId == null) {
            return;
        }

        setUsers((prev) => {
            return prev.map((user) => {
                const isMatch = normaliseUsername(user?.Username) === username;

                if (!(isMatch && user?.IsPendingCreate === true)) {
                    return user;
                }

                return mapCompletedPendingUserRow(user, newId);
            });
        });

        // Update packages if the backend returned updated data
        if (payload?.packages) {
            props.item.Packages = payload.packages.map((pkg) => ({
                Id: pkg.id,
                ResellerPackageId: pkg.resellerPackageId,
                PackageId: pkg.packageId,
                Name: pkg.packageName,
                PackageName: pkg.packageName,
                PUP: pkg.pup,
                PackageType: pkg.packageType,
                Seats: pkg.seatQuantity,
                SeatQuantity: pkg.seatQuantity,
                SeatBlockSize: pkg.seatBlockSize,
                Level: pkg.level,
                CanDelete: pkg.canDelete
            }));
            setPackageVersion((v) => v + 1);
        }
    }, [showError, props.item]);

    useSignalRGroupHandler({
        connection,
        registerHandler,
        ready,
        groupName: signalRGroupName,
        messageType: "CompanyUserManagementAddUser",
        handler: handleUserAddedResultMessage
    });

    // Effects
    useEffect(() => {
        if (ready && data) {
            const options = getFetchOptions({
                method: "GET"
            });

            execute(`api/CustomerUsers/${data.CompanyId}`, options, (error, usersArray) => {
                if (error) {
                    showError("Error", error.message);
                }
                else {
                    const loadedUsers = (usersArray ?? []).map(mapLoadedUserToRow);

                    setUsers(loadedUsers);

                    const hasCustomerAdminWithLogin = (usersArray ?? []).some((user) =>
                        user.RoleId === CompanyUserRole.CustomerAdmin &&
                        user.LastLoginDate != null
                    );

                    setCustomerAdminHasLoggedIn(hasCustomerAdminWithLogin);
                }
            });
        }
    }, [data, ready, execute, showError]);

    useEffect(() => {
        if (ready && resending) {
            const resendContactEmail = data.ContactEmail;
            const options = getFetchOptions({
                method: "POST",
                body: resendContactEmail
            });

            execute("/api/Provisioning/ResendRegistrationEmail", options, (error) => {
                setResending(false);

                if (error) {
                    setActiveModal(MODAL.FATAL_ERROR);
                    return;
                }

                props.setModal(false);
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, props.item, ready, resending, execute]);

    // Customer and package handlers
    function handleChangeInput(fields) {
        setPackageData((prev) => {
            return { ...prev, ...fields };
        });
    }

    function handleSubmitPackages(enable) {
        setIsSubmitting(true);

        const pkgs = packageData.currentCustomerPackages.map((pkg) => ({
            Id: pkg.Id ?? 0,
            PackageId: pkg.PackageId ?? pkg.ParentId ?? 0,
            Level: pkg.Level ?? 0,
            LevelId: pkg.LevelId ?? 0,
            Name: pkg.Name ?? pkg.PackageName ?? "",
            SeatBlocks: pkg.SeatQuantity ?? (typeof pkg.Seats === "string" ? parseInt(pkg.Seats) || 0 : pkg.Seats ?? 0),
            SeatBlockSize: pkg.SeatBlockSize ?? 0
        }));

        const options = getFetchOptions({
            method: "POST",
            body: {
                CustomerId: props.item.Id,
                Packages: pkgs
            }
        });

        execute("/api/CustomerPackage", options, (err) => {
            if (!err) {
                if (typeof props.onPackagesUpdate === "function") {
                    props.onPackagesUpdate(packageData.currentCustomerPackages);
                }
            }

            enable();
            setIsSubmitting(false);
            setCanSubmit(false);
        });
    }

    function handleCloseViewModal(modal) {
        props.setModal(modal);
    }

    function handleRequestDeleteCustomer() {
        setActiveModal(MODAL.CONFIRM_DELETE_CUSTOMER);
    }

    function handleRequestResendRegistrationEmail() {
        setActiveModal(MODAL.CONFIRM_RESEND);
    }

    function handleConfirmResendRegistrationEmail(yes) {
        if (yes) {
            setResending(true);
        }

        setActiveModal(MODAL.NONE);
    }

    function handleConfirmDeleteCustomer(yes) {
        if (yes) {
            const options = getFetchOptions({
                method: "DELETE"
            });

            execute("/api/Customer/" + props.item.Id, options, (error) => {
                props.getDataFromServer();

                if (error) {
                    showError("Error", error.message);
                    return;
                }

                props.setModal(false);
            });
        }

        setActiveModal(MODAL.NONE);
    }

    const handleCloseFatalErrorModal = () => {
        setResending(false);
        setActiveModal(MODAL.NONE);
        window.location.reload();
    };

    // User modal state handlers
    const handleOpenEditModal = useCallback((clickedUser) => {
        setSelectedUser(clickedUser);
        setActiveModal(MODAL.EDIT_USER);
    }, []);

    const handleCloseEditModal = useCallback(() => {
        setSelectedUser(null);
        setActiveModal(MODAL.NONE);
    }, []);

    const handleOpenAddUserModal = useCallback(() => {
        setSelectedUser({
            Id: null,
            FirstName: "",
            LastName: "",
            Username: "",
            RoleId: CompanyUserRole.Learner
        });
        setActiveModal(MODAL.ADD_USER);
    }, []);

    const handleCloseAddUserModal = useCallback(() => {
        setSelectedUser(null);
        setActiveModal(MODAL.NONE);
    }, []);

    const handleRequestUserDelete = useCallback((user) => {
        setUserPendingDelete(user);
        setActiveModal(MODAL.CONFIRM_DELETE_USER);
    }, []);

    const handleRequestSave = useCallback((userDraft, context) => {
        setPendingSave({
            user: userDraft,
            isAdd: !!context?.isAdd,
            enable: context?.enable
        });
        setActiveModal(MODAL.CONFIRM_SAVE);
    }, []);

    // User CRUD handlers
    const handleDeleteUser = useCallback((user) => {
        const body = mapDeleteUserRequestBody(user);
        const options = getFetchOptions({ method: "DELETE", body });

        execute("/api/customer-users", options, (error) => {
            if (error) {
                showError("Error", error.message || "Failed to update user.");
                return;
            }

            setUsers((prev) => {
                const key = (u) => u?.Id ?? (u?.Username ?? u?.Email);
                const doomedKey = key(user);
                return prev.filter((u) => key(u) !== doomedKey);
            });

            setSelectedUser(null);
            setUserPendingDelete(null);
            setActiveModal(MODAL.NONE);
        });
    }, [execute, showError]);

    const handleConfirmDeleteUser = useCallback((yes) => {
        if (yes && userPendingDelete) {
            handleDeleteUser(userPendingDelete);
            return;
        }

        setUserPendingDelete(null);
        setActiveModal(MODAL.NONE);
    }, [handleDeleteUser, userPendingDelete]);

    const handleUserAdded = useCallback((user, companyId) => {
        const body = mapAddUserRequestBody(user, companyId, signalRGroupName);
        const options = getFetchOptions({ method: "POST", body });

        execute("/api/customer-users", options, (error) => {
            if (error) {
                if (error?.message?.Message === "ERR_USER_ALREADY_EXISTS") {
                    showError("Cannot add user", "Email address is already in use on ucSKILLS");
                }
                else {
                    showError(
                        "Error",
                        error?.message?.Message ??
                        error?.message ??
                        "Failed to add user."
                    );
                }

                return;
            }

            const pendingUserRow = mapPendingUserToRow(mapUserToCleanUser(user));

            setUsers((prev) => {
                const targetUsername = (pendingUserRow.Username ?? "").trim().toLowerCase();

                const filtered = prev.filter((existingUser) => {
                    const existingUsername = (existingUser?.Username ?? "").trim().toLowerCase();
                    return existingUsername !== targetUsername;
                });

                return [...filtered, pendingUserRow];
            });

            setSelectedUser(null);
            setActiveModal(MODAL.NONE);
        });
    }, [execute, signalRGroupName, showError]);

    const handleUserSaved = useCallback((updatedUser, companyId) => {
        if (!updatedUser) {
            return;
        }

        const body = mapUpdateUserRequestBody(updatedUser, companyId);
        const options = getFetchOptions({ method: "PUT", body });

        execute("/api/customer-users", options, (error) => {
            if (error) {
                showError("Error", error.message || "Failed to update user.");
                return;
            }

            setSelectedUser(null);
            setActiveModal(MODAL.NONE);
        });

        setUsers((prev) => {
            const key = (u) => u?.Id ?? u?.Username;
            const targetKey = key(updatedUser);
            return prev.map((u) => (key(u) === targetKey ? { ...u, ...updatedUser } : u));
        });
    }, [execute, showError]);

    const handleConfirmSave = useCallback((yes) => {
        pendingSave?.enable?.();

        if (yes && pendingSave?.user) {
            if (pendingSave.isAdd) {
                handleUserAdded(pendingSave.user, data?.CompanyId);
            }
            else {
                handleUserSaved(pendingSave.user, data?.CompanyId);
            }
        }
        else {
            setActiveModal(MODAL.NONE);
        }

        setPendingSave(null);
    }, [pendingSave, data?.CompanyId, handleUserAdded, handleUserSaved]);

    // Render fragments
    const viewContent = (
        <>
            {(loadingCustomer || loadingPackages || isSubmitting) ? (
                <LoadingSpinner active={(loadingCustomer || loadingPackages || isSubmitting)} />
            ) : !data ? (
                <div className="view-customer-no-data-message">
                    <p>
                        We’re sorry, but we can’t retrieve this customer’s data at the moment.
                        The issue has been reported to iTEL’s development team and is being investigated.
                        Please try again shortly.
                    </p>
                </div>
            ) : (
                <div>
                    <h4>Name</h4>
                    <p className="mt-0">{data.Name}</p>

                    <SelectPackages
                        key={packageVersion}
                        {...data}
                        selectedPackages={props.item?.Packages}
                        packageData={availablePackages}
                        onChangeInput={handleChangeInput}
                        isValid={setCanSubmit}
                        disableUserModification={disableUserModification}
                        title={"Package Details"}
                    />

                    <CustomerUsersPanel
                        users={users}
                        onAddUser={handleOpenAddUserModal}
                        onEditUser={handleOpenEditModal}
                    />
                </div>
            )}
        </>
    );

    const buttons = (
        <>
            {!disableUserModification && (
                data ? (
                    <Button type={ButtonType.Tertiary} label={`Delete ${data.Name}`} onClick={handleRequestDeleteCustomer} />
                ) : (
                    <Button type={ButtonType.Tertiary} label={"Delete"} onClick={handleRequestDeleteCustomer} />
                )
            )}

            {data && !customerAdminHasLoggedIn && (
                <Button
                    type={ButtonType.Secondary}
                    icon={faEnvelope}
                    label={"Resend Registration Email"}
                    onClick={handleRequestResendRegistrationEmail}
                />
            )}

            {!disableUserModification && (
                <Button
                    type={ButtonType.Primary}
                    disabled={!canSubmit || isSubmitting}
                    icon={faCircleCheck}
                    label={"Confirm Packages"}
                    onClick={(e, c) => handleSubmitPackages(c)}
                    disableUntilClickCompleted={true}
                />
            )}
        </>
    );

    const isOverlayOpen =
        activeModal === MODAL.CONFIRM_SAVE ||
        activeModal === MODAL.EDIT_USER ||
        activeModal === MODAL.ADD_USER;

    const viewModal = (
        <Modal
            title="Customer Details"
            showModal={props.showModal && !isOverlayOpen}
            content={viewContent}
            size="l"
            modalButtons={buttons}
            close={handleCloseViewModal}
        />
    );

    // Main render
    const render = () => {
        if (reason !== LogoutReason.None) {
            return <AuthLogoutMessage reason={reason} />;
        }

        if (executingFetch) {
            return <LoadingSpinner active={executingFetch} />;
        }

        if (activeModal === MODAL.CONFIRM_RESEND && data) {
            return (
                <MessageModal title="Confirm Resend" type={MessageModalType.Question} onClick={handleConfirmResendRegistrationEmail} size="m">
                    Are you sure you want to resend the registration email to {data.Name}?
                </MessageModal>
            );
        }

        if (activeModal === MODAL.FATAL_ERROR) {
            return (
                <MessageModal title="An Error Occured" type={MessageModalType.Information} size="m" onClick={handleCloseFatalErrorModal}>
                    An error occurred when trying to resend the registration email. Please try again. If the problem persists, please contact ITEL.
                </MessageModal>
            );
        }

        if (activeModal === MODAL.ERROR) {
            return (
                <MessageModal title={errorTitle} onClick={closeActiveModal} size="m">
                    <p>{errorMessage}</p>
                </MessageModal>
            );
        }

        if (activeModal === MODAL.CONFIRM_DELETE_CUSTOMER) {
            return (
                <MessageModal title="Confirm Customer Deletion" type={MessageModalType.Question} onClick={handleConfirmDeleteCustomer} size="m">
                    Are you sure you want to delete {data ? data.Name : "this customer"}?
                </MessageModal>
            );
        }

        if (activeModal === MODAL.CONFIRM_DELETE_USER && userPendingDelete) {
            return (
                <MessageModal
                    title="Confirm User Deletion"
                    type={MessageModalType.Question}
                    onClick={handleConfirmDeleteUser}
                    size="m"
                >
                    Are you sure you want to delete {getUserFullName(userPendingDelete)}?
                </MessageModal>
            );
        }

        return (
            <>
                {viewModal}

                {activeModal === MODAL.CONFIRM_SAVE && (
                    <MessageModal
                        title="Confirm Save"
                        type={MessageModalType.Question}
                        onClick={handleConfirmSave}
                        size="m"
                    >
                        Are you sure you want to save these changes?
                    </MessageModal>
                )}

                {activeModal === MODAL.EDIT_USER && selectedUser && (
                    <EditCustomerUser
                        title={"Edit User"}
                        open={true}
                        showModal={true}
                        selectedUser={selectedUser}
                        setOpenEditor={() => setActiveModal(MODAL.NONE)}
                        onCancel={handleCloseEditModal}
                        onSaved={(updatedUser) => handleUserSaved(updatedUser, data?.CompanyId)}
                        onRequestDelete={handleRequestUserDelete}
                        allowDelete={users.length > 1}
                        roleLabelFn={roleLabel}
                        roleOptions={AssignableRoleOptionsBase}
                        emailReadOnly={true}
                    />
                )}

                {activeModal === MODAL.ADD_USER && (
                    <EditCustomerUser
                        title={"Add User"}
                        open={true}
                        showModal={true}
                        selectedUser={selectedUser}
                        setOpenEditor={() => setActiveModal(MODAL.NONE)}
                        onCancel={handleCloseAddUserModal}
                        onSaved={(newUser) => handleUserAdded(newUser, data?.CompanyId)}
                        onRequestSave={handleRequestSave}
                        allowDelete={true}
                        roleLabelFn={roleLabel}
                        roleOptions={AssignableRoleOptionsBase}
                        emailReadOnly={false}
                    />
                )}
            </>
        );
    };

    return <>{render()}</>;
};

export default ViewCustomer;